import { Navigation } from "@/components/Navigation";
import { Hero } from "@/components/Hero";
import { PrivateServicesTab } from "@/components/PrivateServicesTab";
import { NHSServicesTab } from "@/components/NHSServicesTab";
import { ContactSection } from "@/components/ContactSection";
import { EvaChatbot } from "@/components/EvaChatbot";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LocalBusinessSchema, FAQSchema, OrganizationSchema } from "@/components/seo/JsonLd";
import { useSEO } from "@/hooks/useSEO";

const faqs = [
  {
    question: "Do I need a GP referral for private services?",
    answer: "No. You can book directly with Wilberforce Pharmacy for all our private services. Our pharmacists are qualified to assess and treat eligible patients.",
  },
  {
    question: "Are consultations confidential?",
    answer: "Yes. All services are provided in a private consultation room. We take your privacy seriously and maintain strict confidentiality.",
  },
  {
    question: "Do you offer Travel Vaccinations service?",
    answer: "We offer a wide range of vaccinations, anti-malarials and travel accessories. Also, non travel vaccinations like Chicken Pox and DTP are available. If you are planning to go abroad or need a vaccination for occupational purposes, contact us for a free consultation.",
  },
  {
    question: "What is the Smoking Cessation Service?",
    answer: "If you have decided to quit smoking and looking for someone to help you on this journey then you have come to the right place. Research has shown that you are more likely to be successful at quitting smoking with counseling and Nicotine Replacement Therapies (NRTs) than trying to quit without support.",
  },
  {
    question: "How does Weight Loss Treatment work?",
    answer: "Being overweight puts you at risk of numerous health problems and can affect your confidence. Weight loss can help reduce the risk of a wide range of health problems and enables you to lead a healthier lifestyle. We provide professional guidance and treatment options.",
  },
];

export default function Index() {
  useSEO({
    title: "Wilberforce Pharmacy - Expert Healthcare Services in Hull",
    description: "NHS and private healthcare in Hull: travel vaccinations, B12 shots, blood tests, weight management and prescriptions. Book online.",
    path: "/",
  });
  return (
    <div className="min-h-screen bg-background">
      <OrganizationSchema />
      <LocalBusinessSchema />
      <FAQSchema faqs={faqs} />
      <Navigation />
      <main>
      <Hero />
      
      <section id="services" className="py-20 px-4">
        <div className="container mx-auto space-y-12">
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Our Healthcare Services
            </h2>
            <p className="text-xl text-muted-foreground">
              Expert NHS and private healthcare services tailored to your needs
            </p>
          </div>

          <Tabs defaultValue="nhs" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="nhs" className="text-base">NHS Services</TabsTrigger>
              <TabsTrigger value="private" className="text-base">Private Services</TabsTrigger>
            </TabsList>
            
            <TabsContent value="nhs">
              <div id="nhs-services">
                <NHSServicesTab />
              </div>
            </TabsContent>
            
            <TabsContent value="private">
              <div id="private-services">
                <PrivateServicesTab />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-muted-foreground">
              Everything you need to know about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-lg font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <ContactSection />
      </main>

      <footer className="py-8 px-4 border-t border-border/50">
        <div className="container mx-auto text-center space-y-4">
          <div className="flex items-center justify-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
              <span className="text-white font-bold text-lg">W</span>
            </div>
            <h3 className="text-xl font-bold text-foreground">Wilberforce Pharmacy</h3>
          </div>
          <p className="text-muted-foreground">
            Your trusted healthcare partner in Hull
          </p>
          <p className="text-sm text-muted-foreground">
            © 2025 Wilberforce Pharmacy. All rights reserved.
          </p>
        </div>
        <EvaChatbot />
      </footer>
    </div>
  );
}